import styled, { css } from 'styled-components';
import DefinitionTooltip from '../../DefinitionTooltip';
import { HiShoppingBag } from 'react-icons/hi';
import { SpateAiIcon } from '@/ui/design-system/components';
import { TbReportMoney } from 'react-icons/tb';
import { UnstyledButton } from 'utils/fragments';
import dayjs from 'dayjs';
import { getSentimentBadgeProps } from '../utils';
import { tooltips } from '@/copy';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const BadgesWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
`;
const BadgeStyles = css`
    background-color: ${({ theme }) => theme.gray_100};
    border-radius: 6.25rem;
    border-color: ${({ theme }) => theme.gray_100};
    min-height: 1.5em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-width: 1px;
    border-style: solid;
    padding: 0 0.5em;
    font-size: 0.875em;
    font-weight: 600;
    color: ${({ theme }) => theme.black};
    text-transform: capitalize;
    gap: 0.25em;
    line-height: 1.15;
`;
const BadgeWrapper = styled.span`
    ${BadgeStyles}
`;
const BadgeButtonWrapper = styled(UnstyledButton)`
    ${BadgeStyles}

    &:hover, &:focus-visible {
        background-color: ${({ theme }) => theme.gray_300};
        border-color: ${({ theme }) => theme.gray_300};
    }
`;

const BadgeIcon = styled.span`
    font-size: 1rem;
    display: flex;
    align-items: center;
`;

function Badge({ name, Icon, ...props }) {
    return (
        <BadgeWrapper {...props}>
            <BadgeIcon>
                <Icon />
            </BadgeIcon>
            {name}
        </BadgeWrapper>
    );
}

function BadgeButton({ name, Icon, ...props }) {
    return (
        <BadgeButtonWrapper {...props}>
            <BadgeIcon>
                <Icon />
            </BadgeIcon>
            {name}
        </BadgeButtonWrapper>
    );
}

const SentimentBadgeButton = styled(BadgeButton)`
    background-color: ${({ theme }) => theme.white};
    border-color: ${({ theme }) => theme.gray_300};
    color: ${({ $fgColor }) => $fgColor};

    &:hover,
    &:focus-visible {
        background-color: ${({ $bgColor, theme }) => theme[$bgColor]};
        border-color: ${({ $bgColor, theme }) => theme[$bgColor]};
    }
`;

function PaidPostBadge({ name, Icon, week, year, paidTypesMessage, ...props }) {
    return (
        <DefinitionTooltip
            mixpanelMeta={{ contentKey: 'paidPost' }}
            openOnClick={true}
            week={week}
            year={year}
            body={paidTypesMessage}
        >
            <BadgeButton {...props} Icon={Icon} name={name} />
        </DefinitionTooltip>
    );
}

function SentimentBadge({ score, summary, week, year }) {
    const { Icon, bgColor, fgColor, title } = getSentimentBadgeProps(score);
    return (
        <DefinitionTooltip
            mixpanelMeta={{ contentKey: 'sentimentBadge' }}
            openOnClick={true}
            week={week}
            year={year}
            title={title}
            body={summary}
            sourceDataType="tiktok"
            source={
                summary
                    ? tooltips.sentimentSummary.source
                    : tooltips.sentiment_score.source
            }
        >
            <SentimentBadgeButton
                Icon={Icon}
                name={score}
                $bgColor={bgColor}
                $fgColor={fgColor}
            />
        </DefinitionTooltip>
    );
}

function Badges({ post }) {
    const badges = [];
    if (post.sentiment_score || post.sentiment_score === 0) {
        badges.push(
            <SentimentBadge
                key="sentiment"
                score={post.sentimentScoreInt}
                summary={post.sentiment_summary}
                week={post.week}
                year={post.year}
            />,
        );
    }
    if (post.isNew) {
        badges.push(<Badge key="new" name="new" Icon={SpateAiIcon} />);
    }
    if (post.isPaid)
        badges.push(
            <PaidPostBadge
                key="paid"
                name="paid"
                Icon={TbReportMoney}
                week={post.week}
                year={post.year}
                paidTypesMessage={post.paidTypesMessage}
            />,
        );
    if (post.isShop)
        badges.push(
            <PaidPostBadge
                key="shop"
                name="shop"
                Icon={HiShoppingBag}
                week={post.week}
                year={post.year}
                paidTypesMessage={tooltips.isTikTokShop}
            />,
        );

    return <BadgesWrapper>{badges}</BadgesWrapper>;
}

export default Badges;
